import React from 'react';

import { Typography } from '@mui/material';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  ExternalToolkitPolicyPanel,
  ExternalToolkitPolicyPanelPlaceholder,
  ExternalToolkitRow,
  IntegrationRowSkeleton,
} from '../integrations';

const FETCH_POLICY = {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-first',
} satisfies Parameters<typeof Apollo.useGetExternalToolkitsQuery>[0];
const AGENT_CONNECTOR_SKELETON_COUNT = 2;
const useStyles = makeStyles()(theme => ({
  agentToolsGroups: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  sectionError: { color: theme.palette.text.secondary },
}));

export const ConnectorsContent = React.memo(function ConnectorsContent({
  failed,
  loading,
  toolkits,
}: {
  failed: boolean;
  loading: boolean;
  toolkits: readonly Apollo.ExternalToolkitSimpleFragment[];
}): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const isOrgAdmin = currentUser?.role === Apollo.OrgRole.Admin;
  const {
    data: policyData,
    error: policyError,
    loading: policyLoading,
  } = Apollo.useGetExternalToolkitPoliciesQuery({
    ...FETCH_POLICY,
    skip: !isOrgAdmin,
  });
  const policies = policyData?.externalToolkitPolicies ?? [];

  if (failed)
    return (
      <Typography className={classes.sectionError} variant="body2">
        We could not load your agent connectors. Refresh this page to try again.
      </Typography>
    );

  if (loading && toolkits.length === 0)
    return (
      <div className={classes.agentToolsGroups}>
        {Array.from({ length: AGENT_CONNECTOR_SKELETON_COUNT }).map((_, index) => (
          <IntegrationRowSkeleton key={`agent-connector-skeleton-${index}`} />
        ))}
      </div>
    );

  return (
    <div className={classes.agentToolsGroups}>
      {toolkits.map(toolkit => {
        const policy = policies.find(row => row.slug === toolkit.slug);
        return (
          <ExternalToolkitRow
            key={toolkit.slug}
            isOrgAdmin={isOrgAdmin}
            orgAction={getToolkitOrgAction({
              isOrgAdmin,
              policy,
              policyError: Boolean(policyError),
              policyLoading,
            })}
            toolkit={toolkit}
          />
        );
      })}
    </div>
  );
});

function getToolkitOrgAction({
  isOrgAdmin,
  policy,
  policyError,
  policyLoading,
}: {
  readonly isOrgAdmin: boolean;
  readonly policy: Apollo.ExternalToolkitPolicyRowSimpleFragment | undefined;
  readonly policyError: boolean;
  readonly policyLoading: boolean;
}): JSX.Element | undefined {
  if (!isOrgAdmin) return undefined;
  if (policy) return <ExternalToolkitPolicyPanel policy={policy} />;
  if (policyLoading || policyError)
    return <ExternalToolkitPolicyPanelPlaceholder failed={policyError} />;
  return undefined;
}
